/** @format */
import { Icon, IconVariant } from '@mpb-com/feather/components/atoms/icon';
import { animated } from '@react-spring/web';
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent, MouseEvent, useEffect } from 'react';
import styled from 'styled-components';

import ExpandIcon from '@toucan/client/global/icons/expand.svg';

import {
    Pagination,
    PaginationVariant,
} from '@toucan/client/shared/components/pagination';

import { styles } from './carousel.styles';
import { NavigationButton } from './components/navigation-button';
import { NavigationButtonDirection } from './components/navigation-button/types';
import { ZoomableElement } from './components/zoomable-element';
import { CarouselProps, CarouselVariant } from './types';
import { useSlide } from './use-slide';

export const DEFAULT_TEST_ID = 'carousel-base';

const StyledCarousel = styled.div`
    ${styles}
`;

export const CarouselBase: FunctionComponent<CarouselProps> = ({
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    variant = CarouselVariant.MINIMAL,
    paginationVariant = PaginationVariant.DOT,
    showPagination = true,
    showExpandButton = false,
    imageAspectRatio = '4/3',
    initialPage = 0,
    paginationItems = [],
    items,
    maxSlideHeight,
    onFocussedItemClick,
    onActiveIndexChange,
    note,
    ...otherProps
}: CarouselProps) => {
    const {
        props,
        ulRef,
        handlePageChange,
        activeIndex,
        bind,
        getSlideStyles,
        showNextButton,
        showPrevButton,
    } = useSlide({
        items,
        variant,
        imageAspectRatio,
        initialPage,
        maxSlideHeight,
    });

    const { t } = useTranslation();

    const shouldShowControlPanel = showPagination || showExpandButton;

    const handleItemClick = (index: number) => {
        if (index !== activeIndex) {
            handlePageChange(index);
            return;
        }

        onFocussedItemClick?.(index);
    };

    const handleNavigationClick = (
        index: number,
        event: MouseEvent<HTMLButtonElement>
    ) => {
        event.preventDefault();
        handlePageChange(index);
    };

    useEffect(() => {
        onActiveIndexChange?.(activeIndex);
    }, [activeIndex]);

    return (
        <StyledCarousel
            imageAspectRatio={imageAspectRatio}
            items={items}
            variant={variant}
            paginationVariant={paginationVariant}
            showNextButton={showNextButton}
            showPrevButton={showPrevButton}
            data-testid={dataTestId}
            {...otherProps}
        >
            <div className="slide-container">
                <ul
                    className="carousel-ul"
                    ref={ulRef}
                    data-testid={`${dataTestId}__ul`}
                >
                    {props.map(({ x, scale, opacity }, index) => {
                        const isFocused = activeIndex === index;
                        const key = index; // undesirable but there is no alternative as we need to prevent the animated.li from re-rendering and we have no knowledge on what the items array could contain

                        return (
                            <animated.li
                                {...bind()}
                                key={key}
                                className="carousel-item"
                                style={getSlideStyles({
                                    x,
                                    scale,
                                    opacity,
                                    index,
                                })}
                            >
                                <ZoomableElement
                                    data-slide-index={index}
                                    data-slide-focused={isFocused}
                                    onClick={() => handleItemClick(index)}
                                    data-testid={`${dataTestId}__slide`}
                                >
                                    {items[index]}
                                </ZoomableElement>
                            </animated.li>
                        );
                    })}
                </ul>

                {showPrevButton && (
                    <NavigationButton
                        className="button previous"
                        title={t('Previous')}
                        onClick={(event) =>
                            handleNavigationClick(activeIndex - 1, event)
                        }
                        data-testid={`${dataTestId}__previous-button`}
                        direction={NavigationButtonDirection.LEFT}
                    />
                )}
                {showNextButton && (
                    <NavigationButton
                        className="button next"
                        title={t('Next')}
                        onClick={(event) =>
                            handleNavigationClick(activeIndex + 1, event)
                        }
                        data-testid={`${dataTestId}__next-button`}
                        direction={NavigationButtonDirection.RIGHT}
                    />
                )}
            </div>
            {shouldShowControlPanel && (
                <div className="control-panel">
                    {showPagination && (
                        <Pagination
                            items={paginationItems}
                            currentItem={activeIndex}
                            variant={paginationVariant}
                            onPage={handlePageChange}
                            data-testid={`${dataTestId}__pagination`}
                            showFirstLastButtons={false}
                            showPrevNextButtons={false}
                            className="carousel-pagination"
                            loop={false}
                        />
                    )}
                    {showExpandButton && (
                        <Icon
                            variant={IconVariant.ACTIONABLE}
                            onClick={() => {
                                onFocussedItemClick?.(activeIndex);
                            }}
                            type="button"
                            data-testid={`${dataTestId}__expand-button`}
                            className="button full-screen"
                        >
                            <ExpandIcon />
                        </Icon>
                    )}
                </div>
            )}
            {note && (
                <p
                    data-testid={`${dataTestId}__note`}
                    className="carousel-note"
                >
                    {note}
                </p>
            )}
        </StyledCarousel>
    );
};
